import { Component, inject, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MyOskStudentsService } from '../../../../api/api-services/my-osk-students.service';
import { map, Observable, Subject, Subscription } from 'rxjs';
import { SortOrder } from '../../../../api/sort-order.model';
import { FASDynamicTableColumnConfig } from 'fas-services';
import { getSubscriptionStatus } from '../../../shared/helpers/subscription-status';
import { MyOskSubscriptionsService } from '../../../../api/api-services/my-osk-subscriptions.service';
import { IMyOskSubscriptonsVm } from '../../../../api/my-osk-subscriptons-vm.model';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { StudentsDetailsComponent } from '../students-details/students-details.component';
import {
  centerDynamicModalConfig,
  rightDynamicModalConfig,
} from '../../../shared/helpers/modal.config';
import { eModalRoles } from '../../../shared/models/modals';
import { IMyOskStudentVm } from '../../../../api/my-osk-student-vm.model';
import { StudentsAssignSubscriptionComponent } from '../students-assign-subscription/students-assign-subscription.component';
import { IMyOskStudentRemoveResultVm } from '../../../../api/my-osk-student-remove-result-vm.model';
import { MenuItem, MessageService } from 'primeng/api';
import { InternalExamDialogComponent } from '../internal-exam/internal-exam-dialog.component';
import { InternalExamSessionMode } from '../internal-exam/internal-exam.models';
import { InternalExamResult, InternalExamSource } from '../../../../api/internal-exam-classification.model';
import { Menu } from 'primeng/menu';
import {
  canAdministerStudents,
  canRunLectures,
  canUseDrivingCalendar,
  isOskBoss,
} from '../../../shared/helpers/osk-staff-role';
import { MOBILE_LAYOUT_QUERY } from '../../../shared/services/mobile-shell.service';

@Component({
  selector: 'app-students-list-active',
  templateUrl: './students-list-active.component.html',
  styleUrl: './students-list-active.component.scss',
})
export class StudentsListActiveComponent implements OnInit {
  @Input() resetTable?: Observable<void>;

  private _myOskStudentsService = inject(MyOskStudentsService);
  private _myOskSubscriptionsService = inject(MyOskSubscriptionsService);
  private _primengDialogService = inject(DialogService);
  private _messageService = inject(MessageService);
  private _router = inject(Router);
  public resetTrigger = new Subject<void>();
  public refreshCurrentPage = new Subject<void>();
  public oskSubscriptions$!: Observable<IMyOskSubscriptonsVm[]>;

  public lodingSubscriptions: boolean = false;

  public removeTooltipText:string=''
  public actionMenuItems: MenuItem[] = [];
  /** The boss and the office see the OSK's access packages and manage students. */
  public readonly canAdministerStudents = canAdministerStudents();

  public columns: FASDynamicTableColumnConfig<IMyOskStudentVm>[] = StudentsListActiveComponent.orderForScreen([
    {
      field: 'displayName',
      columnHeader: 'Nazwa użytkownika (Imię i Nazwisko)',
      columnClass: 'flex align-items-center w-full justify-content-between',
      actions: [
        {
          label: 'Szczegóły',
          buttonClass: 'white-button appear',
          icoSuffix: 'pi pi-arrow-right',
          callback: (data: IMyOskStudentVm) => this.showDetails(data),
        },
      ],
    },
    {
      field: 'created',
      dateField: true,
      columnHeader: 'Data utworzenia',
    },
    {
      field: 'email',
      columnHeader: 'Adres email',
    },
    {
      field: 'assignedEmployees',
      columnHeader: 'Prowadzący',
      sortable: false,
      valueFunction: (data: IMyOskStudentVm) =>
        (data.assignedEmployees ?? []).map((employee) => employee.displayName).join(', ') || '—',
    },
    {
      field: 'oskPlatformSubscriptionPlan',
      columnHeader: 'Wykupiony pakiet',
      columnTemplateKey: 'subscriptionPlanTemp',
    },
    {
      field: 'validTo',
      columnHeader: 'Status pakietu',
      columnTemplateKey: 'subscriptionPlanStatusTemp',
    },
    {
      field: 'internalExamResult',
      columnHeader: 'Egzamin wew',
      sortable: false,
      columnTemplateKey: 'internalExamStatusTemp',
    },
    {
      columnHeader: 'Akcje',
      sortable: false,
      columnTemplateKey: 'subscriptionActionsTemp',
    },
  ]);

  private currentPageStudentList: IMyOskStudentVm[] = [];
  private ref: DynamicDialogRef | undefined;
  private subscriptions = new Subscription();

  /**
   * On a phone the row menu ("Akcje") goes right after the name: as the last of eight columns it was a long swipe
   * sideways away. The desktop keeps the order above.
   */
  private static orderForScreen(
    columns: FASDynamicTableColumnConfig<IMyOskStudentVm>[]
  ): FASDynamicTableColumnConfig<IMyOskStudentVm>[] {
    const phone = typeof window.matchMedia === 'function' && window.matchMedia(MOBILE_LAYOUT_QUERY).matches;
    const actions = columns.find((column) => column.columnTemplateKey === 'subscriptionActionsTemp');
    if (!phone || !actions) {
      return columns;
    }
    const rest = columns.filter((column) => column !== actions);
    return [rest[0], actions, ...rest.slice(1)];
  }

  ngOnInit() {
    if (this.canAdministerStudents) {
      this._getOskAvailableSubscriptionsList();
    }
    if (this.resetTable) {
      const resetSub = this.resetTable.subscribe(() => {
        this.resetTrigger.next();
        if (this.canAdministerStudents) {
          this._getOskAvailableSubscriptionsList();
        }
      });
      this.subscriptions.add(resetSub);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();

    if (this.ref) {
      this.ref.close(); // Closes the dialog if it's still open
      this.ref = undefined;
    }
  }

  fetchOskStudentsList = (params: {
    page: number;
    pageSize: number;
    sortField?: keyof IMyOskStudentVm;
    sortOrder?: number;
    search?: string;
  }): Observable<{ data: IMyOskStudentVm[]; total: number }> => {
    const { page, pageSize, sortField = '', sortOrder, search = '' } = params;
    const sortOrderValue =
      sortOrder === 1 ? SortOrder.Ascending : SortOrder.Descending;
    return this._myOskStudentsService
      .get(false, search, sortField, page, pageSize)
      .pipe(
        map((result) => ({
          data: result.data ?? [],
          total: result.allElementsCount,
        }))
      );
  };

  getStatus(subscriptionDate?: Date): { status: string; color: string } {
    return getSubscriptionStatus(subscriptionDate);
  }

  hasInternalExamResult(rowData: IMyOskStudentVm): boolean {
    return rowData.internalExamResult !== undefined &&
      rowData.internalExamResult !== null &&
      rowData.internalExamResult !== InternalExamResult.Undefined;
  }

  isInternalExamPassed(result?: InternalExamResult): boolean {
    return result === InternalExamResult.PassedByOsk ||
      result === InternalExamResult.PassedAutomatically;
  }

  getInternalExamSourceIcon(source?: InternalExamSource | null): string {
    return source === InternalExamSource.Osk ? 'pi-car' : 'pi-home';
  }

  getInternalExamStatusTooltip(rowData: IMyOskStudentVm): string {
    const resultLabels: Partial<Record<InternalExamResult, string>> = {
      [InternalExamResult.PassedByOsk]: 'Zaliczony',
      [InternalExamResult.PassedAutomatically]: 'Zaliczony',
      [InternalExamResult.FailedByOsk]: 'Niezaliczony',
      [InternalExamResult.FailedAutomatically]: 'Niezaliczony',
    };
    const source = rowData.internalExamSource === InternalExamSource.Osk
      ? 'źródło: OSK'
      : 'źródło: egzamin własny';
    return `${resultLabels[rowData.internalExamResult!] ?? ''} — ${source}`;
  }

  getRemoveStudenTooltip(data: IMyOskStudentVm): string {
    return data.validTo===null ?'Usuń' : 'Usunięcie konta możliwe jest do czasu aż użytkownik aktywuje dostęp';
  }

  openActionsMenu(
    event: Event,
    rowData: IMyOskStudentVm,
    rowIndex: number,
    menu: Menu
  ): void {
    // What the role may do: exams and removing students - the boss; lectures - the boss, instructors and
    // lecturers; access and student data - the boss and the office. The API checks the same.
    const boss = isOskBoss();
    this.actionMenuItems = [
      {
        label: 'Rozpocznij egzamin wewnętrzny',
        icon: 'pi pi-play',
        visible: boss,
        command: () => this.customRowAction(rowData, rowIndex, 'internal-exam'),
      },
      {
        label: 'Rozpocznij wykłady',
        icon: 'pi pi-video',
        visible: canRunLectures(),
        command: () => this.customRowAction(rowData, rowIndex, 'lectures'),
      },
      // The calendar opens "Umów jazdę" for the student (?bookStudent) - the dialog stays out of this eager module.
      {
        label: 'Umów jazdę',
        icon: 'pi pi-calendar-plus',
        visible: canUseDrivingCalendar(),
        command: () => this._router.navigate(['/calendar'], { queryParams: { bookStudent: rowData.guid } }),
      },
      {
        label: 'Przydziel abonament',
        icon: 'pi pi-briefcase',
        visible: this.canAdministerStudents,
        command: () => this.customRowAction(rowData, rowIndex, 'add-subscription'),
      },
      {
        label: this.canAdministerStudents ? 'Edytuj' : 'Szczegóły',
        icon: this.canAdministerStudents ? 'pi pi-pencil' : 'pi pi-eye',
        command: () => this.customRowAction(rowData, rowIndex, 'edit'),
      },
      {
        label: 'Usuń',
        icon: 'pi pi-trash',
        visible: boss,
        disabled: rowData.validTo !== null,
        command: () => this.customRowAction(rowData, rowIndex, 'delete'),
      },
    ];
    menu.toggle(event);
  }

  studentListLoaded(data: IMyOskStudentVm[]) {
    this.currentPageStudentList = data;
  }

   customRowAction(
    rowData: IMyOskStudentVm,
    rowIndex: number,
    type: 'edit' | 'delete' | 'add-subscription' | 'internal-exam' | 'lectures'
  ): void {
    switch (type) {
      case 'edit':
        this.showDetails(rowData);
        break;
      case 'add-subscription':
        this.openAssignSubscriptionModal(rowData);
        break;
      case 'delete':
        this.deleteStudent(rowData);
        break;
      case 'internal-exam':
        this.openInternalExamModal(rowData, 'exam');
        break;
      case 'lectures':
        this.openInternalExamModal(rowData, 'lectures');
        break;
      default:
        break;
    }
  }

  private _getOskAvailableSubscriptionsList(): void {
    this.oskSubscriptions$ = this._myOskSubscriptionsService.get();
  }

  private showDetails(data: IMyOskStudentVm): void {
    const index =
      this.currentPageStudentList.findIndex(
        (item) => item.guid === data.guid
      ) ?? 0;
    this.openStudentDetailsModal(data.guid, index, this.currentPageStudentList);
  }

  private openStudentDetailsModal(
    selectedStudentGuid: string | null,
    currentStudentIndex: number,
    studentListPerPage: IMyOskStudentVm[]
  ) {
    this.ref = this._primengDialogService.open(StudentsDetailsComponent, {
      ...rightDynamicModalConfig,
      data: {
        currentStudentIndex,
        studentListPerPage,
      },
    });

    this.ref.onClose.subscribe((res: { status: eModalRoles }) => {
      console.log('Modale with user details closed with status:', res);
      this.refreshCurrentPage.next();
    });
  }

  private openAssignSubscriptionModal(studentDetails: IMyOskStudentVm) {
    this.ref = this._primengDialogService.open(
      StudentsAssignSubscriptionComponent,
      {
        ...centerDynamicModalConfig,
        width: '966px',
        data: {
          studentDetails,
        },
      }
    );

    this.ref.onClose.subscribe({
      next: (res: eModalRoles | undefined) => {
        if (res === eModalRoles.SUCCESS) {
          this._getOskAvailableSubscriptionsList();
          this.refreshCurrentPage.next();
        }
      },
    });
  }

  private deleteStudent(rowData: IMyOskStudentVm): void {
    this._myOskStudentsService.delete(rowData.guid).subscribe({
      next: (res: IMyOskStudentRemoveResultVm) => {
        if (!res.success) {
          this._messageService.add({
            severity: 'info',
            detail: res.message
          });
        } 
        this.refreshCurrentPage.next()

      },
      error: () => {
        this._messageService.add({
          severity: 'error',
          summary: 'Błąd',
          detail: 'Wystąpił błąd. Spróbuj ponownie później.',
        })
      }
    })
  }

  private openInternalExamModal(
    student: IMyOskStudentVm,
    mode: InternalExamSessionMode
  ): void {
    this.ref = this._primengDialogService.open(InternalExamDialogComponent, {
      ...centerDynamicModalConfig,
      width: '980px',
      showHeader: true,
      header:
        mode === 'lectures'
          ? 'Rozpocznij wykłady'
          : 'Rozpocznij egzamin wewnętrzny',
      data: {
        studentGuid: student.guid,
        studentDisplayName: student.displayName,
        mode,
      },
    });
  }
}
